import { useEffect, useRef, useCallback, useMemo } from 'react'
import { useSessionStore } from '@/stores'

/**
 * Port of ChatInactivityMixin.js
 * Manages inactivity timer for chatbot, dispatching messages from
 * campaign.chat_inactivity_messages via CustomEvents.
 *
 * Custom events used:
 *   Listens:  'chat-started' (detail: boolean)
 *             'user-replied'
 *             'chatbot-replied'
 *   Emits:    'chatbot-inactivity-message' (detail: string[])
 *             'chat-ended'
 */
export function useChatInactivity() {
  const campaign = useSessionStore(s => s.campaign) as any

  const inactivityTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const chatEndTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const currentMessageIndexRef = useRef(0)
  const chatStartedRef = useRef(false)
  const waitingChatbotToReplyRef = useRef(false)

  const inactivityMessages: any[] = useMemo(() => campaign?.chat_inactivity_messages || [], [campaign])

  const clearTimers = useCallback(() => {
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current)
      inactivityTimeoutRef.current = null
    }
    if (chatEndTimeoutRef.current) {
      clearTimeout(chatEndTimeoutRef.current)
      chatEndTimeoutRef.current = null
    }
  }, [])

  // Declared as a hoisted function so the timeout can re-enter it without the
  // callback referencing its own binding before initialisation.
  const scheduleNextMessage = useCallback(() => {
    function schedule() {
      if (!chatStartedRef.current || !inactivityMessages.length) {
        return
      }

      const chatMessages: any[] = JSON.parse(localStorage.getItem('chat') || '[]')
      if (chatMessages.length < 6) {
        return
      }

      const idx = currentMessageIndexRef.current

      if (idx < inactivityMessages.length) {
        const inactivityMessage = inactivityMessages[idx]
        inactivityTimeoutRef.current = setTimeout(() => {
          window.dispatchEvent(
            new CustomEvent('chatbot-inactivity-message', {
              detail: [inactivityMessage.message],
            })
          )
          clearTimers()
          currentMessageIndexRef.current++
          schedule()
        }, inactivityMessage.duration * 1000)
      } else if (idx === inactivityMessages.length && !chatEndTimeoutRef.current) {
        chatEndTimeoutRef.current = setTimeout(() => {
          window.dispatchEvent(new CustomEvent('chat-ended'))
          clearTimers()
          waitingChatbotToReplyRef.current = false
          currentMessageIndexRef.current = 0
        }, 10000)
      }
    }
    schedule()
  }, [inactivityMessages, clearTimers])

  const resetTimer = useCallback(() => {
    clearTimers()
    waitingChatbotToReplyRef.current = false
    currentMessageIndexRef.current = 0
    scheduleNextMessage()
  }, [clearTimers, scheduleNextMessage])

  const handleUserActivity = useCallback(() => {
    clearTimers()
    if (!chatStartedRef.current || waitingChatbotToReplyRef.current) {
      return
    }

    const chatMessages: any[] = JSON.parse(localStorage.getItem('chat') || '[]')
    if (chatMessages.length < 6) {
      return
    }

    const lastInactivityIdx = inactivityMessages.findIndex((im: any) => {
      return chatMessages[chatMessages.length - 1]?.text?.indexOf(im.message) > -1
    })
    currentMessageIndexRef.current = Math.max(0, lastInactivityIdx + 1)

    if (currentMessageIndexRef.current < inactivityMessages.length) {
      scheduleNextMessage()
    }
  }, [clearTimers, inactivityMessages, scheduleNextMessage])

  useEffect(() => {
    if ((window as any).hasChatInactivityHandler) {
      return
    }

    const onMouseMove = handleUserActivity
    const onKeyPress = handleUserActivity
    const onTouchStart = handleUserActivity

    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('keypress', onKeyPress)
    window.addEventListener('touchstart', onTouchStart)
    ;(window as any).hasChatInactivityHandler = onMouseMove

    const onChatStarted = (e: CustomEvent) => {
      chatStartedRef.current = e.detail as boolean
      handleUserActivity()
    }

    const onUserReplied = () => {
      clearTimers()
      waitingChatbotToReplyRef.current = true
    }

    const onChatbotReplied = () => {
      setTimeout(() => resetTimer(), 500)
    }

    window.addEventListener('chat-started' as any, onChatStarted)
    window.addEventListener('user-replied' as any, onUserReplied)
    window.addEventListener('chatbot-replied' as any, onChatbotReplied)

    return () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('keypress', onKeyPress)
      window.removeEventListener('touchstart', onTouchStart)
      window.removeEventListener('chat-started' as any, onChatStarted)
      window.removeEventListener('user-replied' as any, onUserReplied)
      window.removeEventListener('chatbot-replied' as any, onChatbotReplied)
      ;(window as any).hasChatInactivityHandler = false
      clearTimers()
    }
  }, [handleUserActivity, clearTimers, resetTimer])
}
